import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { takeAdminParam } from '../../lib/adminLinks';
import { isWaitingContact } from '../../lib/waitingRequests';
import { toast } from 'sonner';
import { Mail, FileText, Loader2, ChevronDown, XCircle, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { HelpLink } from './HelpLink';
import { LeadSourceChip } from './ConsultationSource';
import { ContactQuoteLink } from './ContactQuoteLink';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = [['quote', 'Quote requests'], ['unlinked', 'No Sales Quote yet'], ['all', 'All messages']];

const CloseToggle = ({ c, token, onPatch }) => {
  const [busy, setBusy] = useState(false);
  const closed = c.status === 'closed';
  const toggle = async () => {
    if (!closed && !window.confirm('Close this request? It stays in the list and history but no longer counts as waiting for a Sales Quote.')) return;
    setBusy(true);
    try {
      await axios.put(`${API_URL}/admin/contact-messages/${c.id}/status`, { status: closed ? 'open' : 'closed' }, { headers: { Authorization: `Bearer ${token}` } });
      onPatch(c.id, { status: closed ? undefined : 'closed' });
      toast.success(closed ? 'Request reopened' : 'Request closed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <Button size="sm" variant="outline" onClick={toggle} disabled={busy} data-testid={closed ? 'contact-reopen-btn' : 'contact-close-btn'}>
        {closed ? <RotateCcw className="w-4 h-4 mr-1.5" /> : <XCircle className="w-4 h-4 mr-1.5" />}{closed ? 'Reopen request' : 'Close request'}
      </Button>
      <HelpLink topic="waiting-for-quote" />
    </div>
  );
};

const ContactRow = ({ c, token, onPatch, initialOpen }) => {
  const [open, setOpen] = useState(initialOpen);
  const ref = useRef(null);
  useEffect(() => { if (initialOpen) ref.current?.scrollIntoView({ block: 'center' }); }, [initialOpen]);
  return (
    <div ref={ref} className={`border rounded-lg bg-white ${initialOpen ? 'border-amber-400 ring-2 ring-amber-200' : 'border-neutral-200'}`} data-testid={`contact-row-${c.id}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-start justify-between gap-4 p-4 text-left hover:bg-neutral-50 transition-colors" data-testid={`contact-toggle-${c.id}`}>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{c.name}</span>
            {c.is_quote_request && <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1"><FileText className="w-3 h-3" />Quote request</span>}
            {c.linked_quote && <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700" data-testid={`contact-linked-badge-${c.id}`}>Sales Quote linked</span>}
            {c.status === 'closed' && <span className="px-2 py-0.5 text-xs rounded-full bg-neutral-200 text-neutral-700" data-testid={`contact-closed-badge-${c.id}`}>Closed</span>}
          </div>
          <p className="text-sm text-neutral-600 truncate">{c.subject || 'No subject'}</p>
          <p className="text-xs text-neutral-500">{c.email}{c.phone ? ` · ${c.phone}` : ''} · {c.created_at ? new Date(c.created_at).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' }) + ' ET' : ''}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-neutral-400 mt-1 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-neutral-100 pt-3">
          {c.project_type && <p className="text-xs text-neutral-500">Project type: <span className="text-neutral-800">{c.project_type}</span></p>}
          <p className="text-sm whitespace-pre-wrap text-neutral-800" data-testid={`contact-message-${c.id}`}>{c.message}</p>
          <LeadSourceChip c={c} hideIfMissing testPrefix="contact-source" />
          {c.is_quote_request && <ContactQuoteLink contact={c} token={token} onChange={(p) => onPatch(c.id, p)} />}
          {c.is_quote_request && <CloseToggle c={c} token={token} onPatch={onPatch} />}
        </div>
      )}
    </div>
  );
};

export const ContactMessagesTab = ({ token }) => {
  const [focusId] = useState(() => takeAdminParam('contact'));
  const [filter, setFilter] = useState(() => (takeAdminParam('filter') === 'unlinked' ? 'unlinked' : focusId ? 'all' : 'quote'));
  const [items, setItems] = useState(null);
  useEffect(() => {
    setItems(null);
    axios.get(`${API_URL}/admin/contact-messages`, { headers: { Authorization: `Bearer ${token}` }, params: { type: filter === 'all' ? undefined : 'quote' } })
      .then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [filter, token]);
  const patch = (id, p) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, ...p } : x)));
  const shown = filter === 'unlinked' ? (items || []).filter(isWaitingContact) : items;
  return (
    <div className="space-y-6" data-testid="contact-messages-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2"><Mail className="w-5 h-5" />Contact Messages <HelpLink topic="contact-messages" /></h2>
          <p className="text-sm text-neutral-500">Messages from website contact and quote forms. Link a quote-form request to its Sales Quote to track conversion.</p>
        </div>
        <div className="inline-flex rounded-full border border-neutral-200 p-1">
          {FILTERS.map(([v, l]) => (
            <button key={v} type="button" onClick={() => setFilter(v)} className={`px-4 py-1.5 text-sm rounded-full transition-colors ${filter === v ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:bg-neutral-100'}`} data-testid={`contact-filter-${v}`}>{l}</button>
          ))}
        </div>
      </div>
      {items === null ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
        : !shown.length ? <p className="text-sm text-neutral-500 py-12 text-center" data-testid="contact-messages-empty">{filter === 'unlinked' ? 'No open quote-form requests are waiting for a Sales Quote.' : 'No messages yet.'}</p>
          : <div className="space-y-3" data-testid="contact-messages-list">{shown.map((c) => <ContactRow key={c.id} c={c} token={token} onPatch={patch} initialOpen={c.id === focusId} />)}</div>}
    </div>
  );
};
